
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import FinancialPerformanceTableFilterable from '@/components/reports-v3/components/filterables/FinancialPerformanceTableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
	FinancialPerformance,
	PermissionsGroup,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import moment, { Moment } from 'moment';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		FinancialPerformanceTableFilterable,
	},
})
export default class BpFinancialPerformanceTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'year'
	> = ['year'];

	public currentYearResult = {} as FinancialPerformance;
	public lastYearResult = {} as FinancialPerformance;
	public last2YearResult = {} as FinancialPerformance;
	public last3YearResult = {} as FinancialPerformance;
	public last4YearResult = {} as FinancialPerformance;

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get selectedYear() {
		return moment(this.selectedDateRange[1]).year();
	}

	public get dateLabel() {
		const startDate = moment(this.selectedDateRange[0]).format('MMM YYYY');
		const endDate = moment(this.selectedDateRange[1]).format('MMM YYYY');
		return `FY ${this.selectedYear} (${startDate} - ${endDate})`;
	}

	// Use selectedDateRange directly as the current year range
	public get currentYearDateRange(): [number, number] {
		return this.selectedDateRange;
	}

	public get lastYearDateRange(): [number, number] {
		return [
			moment(this.selectedDateRange[0]).add(-1, 'year').valueOf(),
			moment(this.selectedDateRange[1]).add(-1, 'year').valueOf(),
		];
	}

	public get last2YearDateRange(): [number, number] {
		return [
			moment(this.selectedDateRange[0]).add(-2, 'year').valueOf(),
			moment(this.selectedDateRange[1]).add(-2, 'year').valueOf(),
		];
	}

	public get last3YearDateRange(): [number, number] {
		return [
			moment(this.selectedDateRange[0]).add(-3, 'year').valueOf(),
			moment(this.selectedDateRange[1]).add(-3, 'year').valueOf(),
		];
	}

	public get last4YearDateRange(): [number, number] {
		return [
			moment(this.selectedDateRange[0]).add(-4, 'year').valueOf(),
			moment(this.selectedDateRange[1]).add(-4, 'year').valueOf(),
		];
	}

	public get last5YearDateRange(): [number, number] {
		return [
			moment(this.selectedDateRange[0]).add(-5, 'year').valueOf(),
			moment(this.selectedDateRange[1]).add(-5, 'year').valueOf(),
		];
	}

	public getMonthValues(dateRange: [number, number]) {
		const [d0, d1] = dateRange.map((d) => moment(d));
		const period = d1.diff(d0, 'month') + 1;
		const result: Moment[] = [];
		for (let i = 0; i < period; i++) {
			result.push(d0.clone());
			d0.add(1, 'month');
		}
		return result;
	}

	// --- Number-based computation helpers ---

	public calcNetSales(sl: number, sa: number): number {
		// SL is credit (negative), SA is debit (positive). Negate to show positive
		return -(sl + sa);
	}

	public calcOtherIncome(oi: number, eo: number): number {
		// OI/EO are credits (negative in GL), negate to show positive
		return -(oi + eo);
	}

	public calcCost(co: number, stockBalance: number): number {
		return co + stockBalance;
	}

	public calcGrossProfit(netSales: number, cost: number): number {
		return netSales - cost;
	}

	public calcNetProfitBeforeTax(
		grossProfit: number, otherIncome: number, ep: number,
	): number {
		return grossProfit + otherIncome - ep;
	}

	public calcRevenueGrowth(
		currentNetSales: number, previousNetSales: number,
	): number {
		return previousNetSales !== 0
			? ((currentNetSales - previousNetSales) / previousNetSales) * 100
			: 0;
	}

	public calcGrossMargin(
		grossProfit: number, netSales: number,
	): number {
		return netSales !== 0 ? (grossProfit / netSales) * 100 : 0;
	}

	public calcNetMargin(
		netPBT: number, netSales: number,
	): number {
		return netSales !== 0 ? (netPBT / netSales) * 100 : 0;
	}

	public calcCashRatio(cashBank: number, cl: number): number {
		// Cash is debit (positive), CL is credit (negative). Negate CL
		return cl !== 0 ? cashBank / (cl * -1) : 0;
	}

	public calcCurrentRatio(ca: number, cl: number): number {
		// CA is debit (positive), CL is credit (negative). Negate CL
		return cl !== 0 ? ca / (cl * -1) : 0;
	}

	public calcDebtRatio(
		totalAsset: number, totalLiability: number,
	): number {
		// Liability is credit (negative). Negate to get positive
		return totalAsset !== 0 ? (totalLiability * -1) / totalAsset : 0;
	}

	public calcReturnOnEquity(
		netPBT: number, equityGL: number,
	): number {
		return equityGL !== 0 ? (netPBT / -equityGL) * 100 : 0;
	}

	public calcLeverageRatio(
		totalLiability: number, equityGL: number, stockBalance: number,
	): number {
		const equity = equityGL + stockBalance;
		return equity !== 0 ? totalLiability / equity : 0;
	}

	public calcFixedAssetToNetRatio(
		fa: number, totalAsset: number, totalLiability: number,
	): number {
		// Net Worth = Assets + (negative liability)
		const net = totalAsset + totalLiability;
		return net !== 0 ? fa / net : 0;
	}

	public calcCurrentLiabilityToNetRatio(
		cl: number, totalAsset: number, totalLiability: number,
	): number {
		const net = totalAsset + totalLiability;
		// CL is negative, negate to get positive
		return net !== 0 ? (cl * -1) / net : 0;
	}

	public calcWorkingCapitalToTotalAssetRatio(
		ca: number, cl: number, totalAsset: number,
	): number {
		// CL is credit (negative). Working Capital = CA + CL
		const net = ca + cl;
		return totalAsset !== 0 ? (net / totalAsset) * 100 : 0;
	}

	public calcInventoryTurnover(
		cost: number, stockMovement2: number,
	): number {
		return stockMovement2 !== 0 ? cost / stockMovement2 : 0;
	}

	public calcTotalAssetTurnover(
		netSales: number, beforeAsset: number,
		afterAsset: number, stockMovement2: number,
	): number {
		const averageAsset = (beforeAsset + afterAsset) / 2 + stockMovement2;
		return averageAsset !== 0 ? netSales / averageAsset : 0;
	}

	public calcEfficiencyRatio(
		ep: number, netSales: number,
	): number {
		return netSales !== 0 ? (ep / netSales) * 100 : 0;
	}

	public calcAccountReceivableTurnOver(
		netSales: number, debtor: number,
	): number {
		return debtor !== 0 ? netSales / debtor : 0;
	}

	public calcAccountPayableTurnOver(
		netSales: number, creditor: number,
	): number {
		// Creditor is credit (negative). Negate
		return creditor !== 0 ? netSales / (creditor * -1) : 0;
	}

	public get expensiveHook() {
		const { selectedDateRange } = this;
		return JSON.stringify([selectedDateRange]);
	}

	public async expensiveCalc() {
		const table = getTableName('transactions');
		const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
		const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';

		const dateRanges: Array<[number, number]> = [
			this.currentYearDateRange,
			this.lastYearDateRange,
			this.last2YearDateRange,
			this.last3YearDateRange,
			this.last4YearDateRange,
		];
		const last5Range = this.last5YearDateRange;

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['transactions', 'stockBalances']);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		// Helper to build accType sum lookup from query results
		const toAccSums = (rows: any[]): Record<string, number> => {
			const sums: Record<string, number> = {};
			for (const row of rows) {
				sums[row.ACCTYPE] = Number(row.total) || 0;
			}
			return sums;
		};
		const toSpecialSums = (rows: any[]): Record<string, number> => {
			const sums: Record<string, number> = {};
			for (const row of rows) {
				sums[row.SPECIALACCTYPE] = Number(row.total) || 0;
			}
			return sums;
		};

		const plAccTypes = `'SL','SA','OI','EO','CO','EP'`;
		const bsAccTypes = `'CA','OA','FA','CL','OL','LT'`;
		const eqAccTypes
			= `'RE','EP','SL','SA','OI','EO','CO','TX','CP','RV','AP'`;

		// Run all queries + stock calls in parallel
		const [
			dateRangeResults,
			last5IncomeResult,
			asOfResults,
			specialResults,
			beforeResults,
			stockMovements,
			stockMovements2,
		] = await Promise.all([
			// 5 dateRange queries: income/cost/expense per year
			Promise.all(dateRanges.map((range) =>
				queryHead.rawQuery(`
					SELECT "ACCTYPE", SUM(${amt}) as total
					FROM ${table}
					WHERE ${dateExpr}
						BETWEEN ${range[0]} AND ${range[1]}
						AND "ACCTYPE" IN (${plAccTypes})
					GROUP BY "ACCTYPE"
				`),
			)),
			// last5Year income only (for year4 revenue growth)
			queryHead.rawQuery(`
				SELECT "ACCTYPE", SUM(${amt}) as total
				FROM ${table}
				WHERE ${dateExpr}
					BETWEEN ${last5Range[0]} AND ${last5Range[1]}
					AND "ACCTYPE" IN ('SL','SA')
				GROUP BY "ACCTYPE"
			`),
			// 5 asOf queries: assets + liabilities + equity combined
			Promise.all(dateRanges.map((range) =>
				queryHead.rawQuery(`
					SELECT "ACCTYPE", SUM(${amt}) as total
					FROM ${table}
					WHERE ${dateExpr} <= ${range[1]}
						AND "ACCTYPE" IN (${bsAccTypes},${eqAccTypes})
					GROUP BY "ACCTYPE"
				`),
			)),
			// 5 specialAccType asOf: cashBank + debtor + creditor
			Promise.all(dateRanges.map((range) =>
				queryHead.rawQuery(`
					SELECT "SPECIALACCTYPE", SUM(${amt}) as total
					FROM ${table}
					WHERE ${dateExpr} <= ${range[1]}
						AND (
							("ACCTYPE" = 'CA'
								AND "SPECIALACCTYPE" IN ('CH','BA','DR'))
							OR ("ACCTYPE" = 'CL'
								AND "SPECIALACCTYPE" = 'CR')
						)
					GROUP BY "SPECIALACCTYPE"
				`),
			)),
			// 5 beforeFinancialStart asset queries
			Promise.all(dateRanges.map((range) =>
				queryHead.rawQuery(`
					SELECT "ACCTYPE", SUM(${amt}) as total
					FROM ${table}
					WHERE ${dateExpr} <= ${range[0] - 1}
						AND "ACCTYPE" IN ('CA','OA','FA')
					GROUP BY "ACCTYPE"
				`),
			)),
			// 5 getStockMovement calls
			Promise.all(dateRanges.map((range) =>
				this._getStockMovement(queryHead, range),
			)),
			// 5 getStockMovement2 calls
			Promise.all(dateRanges.map((range) =>
				this._getStockMovement2(queryHead, range),
			)),
		]);
		this.checkInterrupted(jobId);

		// Parse query results
		const drSums = dateRangeResults.map(toAccSums);
		const last5Sums = toAccSums(last5IncomeResult);
		const aoSums = asOfResults.map(toAccSums);
		const spSums = specialResults.map(toSpecialSums);
		const bfSums = beforeResults.map(toAccSums);

		// Compute per-year derived values
		const yearData = dateRanges.map((range, i) => {
			const dr = drSums[i];
			const ao = aoSums[i];
			const sp = spSums[i];
			const bf = bfSums[i];
			const sm = stockMovements[i];
			const sm2 = stockMovements2[i];

			// P&L sums from dateRange
			const sl = dr.SL || 0;
			const sa = dr.SA || 0;
			const oi = dr.OI || 0;
			const eo = dr.EO || 0;
			const co = dr.CO || 0;
			const ep = dr.EP || 0;

			const netSales = this.calcNetSales(sl, sa);
			const otherIncome = this.calcOtherIncome(oi, eo);
			const cost = this.calcCost(co, sm);
			const grossProfit = this.calcGrossProfit(netSales, cost);
			const netPBT = this.calcNetProfitBeforeTax(
				grossProfit, otherIncome, ep,
			);

			// Balance sheet sums from asOf
			const ca = ao.CA || 0;
			const oa = ao.OA || 0;
			const fa = ao.FA || 0;
			const cl = ao.CL || 0;
			const ol = ao.OL || 0;
			const lt = ao.LT || 0;
			const totalAsset = ca + oa + fa;
			const totalLiability = cl + ol + lt;

			// Equity: sum of 11 accTypes from asOf
			const equityGL = (ao.RE || 0) + (ao.EP || 0)
				+ (ao.SL || 0) + (ao.SA || 0) + (ao.OI || 0)
				+ (ao.EO || 0) + (ao.CO || 0) + (ao.TX || 0)
				+ (ao.CP || 0) + (ao.RV || 0) + (ao.AP || 0);

			// SpecialAccType sums from asOf
			const cashBank = (sp.CH || 0) + (sp.BA || 0);
			const debtor = sp.DR || 0;
			const creditor = sp.CR || 0;

			// Before financial start total asset
			const beforeTotalAsset
				= (bf.CA || 0) + (bf.OA || 0) + (bf.FA || 0);

			return {
				netSales, otherIncome, cost, grossProfit, netPBT,
				ca, fa, totalAsset, cl, totalLiability,
				equityGL, cashBank, debtor, creditor,
				beforeTotalAsset, sm, sm2, ep,
			};
		});

		// last5Year net sales (for year4 revenue growth)
		const last5NetSales = this.calcNetSales(
			last5Sums.SL || 0, last5Sums.SA || 0,
		);

		this.generateLoadingText(1);

		// Build FinancialPerformance result for each year
		const results: FinancialPerformance[] = dateRanges.map(
			(range, i) => {
				const d = yearData[i];
				const prevNetSales = i < 4
					? yearData[i + 1].netSales
					: last5NetSales;

				const result = {} as FinancialPerformance;
				result.year = moment(range[1]).year();

				result.revenueGrowth = this.calcRevenueGrowth(
					d.netSales, prevNetSales,
				);
				result.grossMargin = this.calcGrossMargin(
					d.grossProfit, d.netSales,
				);
				result.netMargin = this.calcNetMargin(
					d.netPBT, d.netSales,
				);
				result.cashRatio = this.calcCashRatio(
					d.cashBank, d.cl,
				);
				result.currentRatio = this.calcCurrentRatio(
					d.ca, d.cl,
				);
				result.debtRatio = this.calcDebtRatio(
					d.totalAsset, d.totalLiability,
				);
				result.returnOnEquity = this.calcReturnOnEquity(
					d.netPBT, d.equityGL,
				);
				result.leverageRatio = this.calcLeverageRatio(
					d.totalLiability, d.equityGL, d.sm,
				);
				result.fixedAssetToNetRatio
					= this.calcFixedAssetToNetRatio(
						d.fa, d.totalAsset, d.totalLiability,
					);
				result.currentLiabilitiesToNetWorth
					= this.calcCurrentLiabilityToNetRatio(
						d.cl, d.totalAsset, d.totalLiability,
					);
				result.workingCapitalToTotalAsset
					= this.calcWorkingCapitalToTotalAssetRatio(
						d.ca, d.cl, d.totalAsset,
					);
				result.inventoryTurnover = this.calcInventoryTurnover(
					d.cost, d.sm2,
				);
				result.totalAssetTurnover = this.calcTotalAssetTurnover(
					d.netSales, d.beforeTotalAsset, d.totalAsset, d.sm2,
				);
				result.efficiencyRatio = this.calcEfficiencyRatio(
					d.ep, d.netSales,
				);
				result.accountReceivableTurnover
					= this.calcAccountReceivableTurnOver(
						d.netSales, d.debtor,
					);
				result.accountPayableTurnover
					= this.calcAccountPayableTurnOver(
						d.netSales, d.creditor,
					);

				return result;
			},
		);

		this.currentYearResult = results[0];
		this.lastYearResult = results[1];
		this.last2YearResult = results[2];
		this.last3YearResult = results[3];
		this.last4YearResult = results[4];

		this.saveHistory(
			'currentYearResult',
			'lastYearResult',
			'last2YearResult',
			'last3YearResult',
			'last4YearResult',
		);
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
